import { graphql, useStaticQuery } from "gatsby";
import { useMemo } from "react";

/**
 * Resolves the site-level Organization config story for JSON-LD.
 *
 * This space is folder-based (`en/`, `ko/`, `zh/`, `ja/`) rather than Storyblok
 * field i18n, so language matching uses `full_slug` with a fallback to English
 * (`en/config/organization`) and then any remaining entry.
 */
export default function useOrganizationConfig(language: Language) {
  const data = useStaticQuery<{
    organizations: {
      edges: {
        node: { full_slug: string; content: string };
      }[];
    };
  }>(graphql`
    query OrganizationConfigQuery {
      organizations: allStoryblokEntry(
        filter: { field_component: { eq: "config_organization" } }
      ) {
        edges {
          node {
            full_slug
            content
          }
        }
      }
    }
  `);

  return useMemo(() => {
    const entries = (data?.organizations?.edges || []).map(({ node }) => ({
      full_slug: node.full_slug,
      content: JSON.parse(node.content) as OrganizationConfigType,
    }));

    const match =
      entries.find(({ full_slug }) => full_slug.startsWith(`${language}/`)) ||
      entries.find(({ full_slug }) =>
        full_slug.endsWith("en/config/organization")
      ) ||
      entries.find(
        ({ full_slug }) =>
          full_slug === "config/organization" ||
          full_slug.endsWith("/config/organization")
      ) ||
      entries[0];

    return match?.content;
  }, [data, language]);
}
